import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import TutorialOverlay from "./TutorialOverlay";
import { getTutorialById } from "./tutorialRegistry";
import {
  calculateSpotlightRect,
  eventMatchesTutorialTarget,
  findTutorialTarget,
  getMatchedTutorialTargetId,
  getToothSideFromTargetId,
  getTutorialInteractiveTargetIds,
  routeMatches,
} from "./tutorialEngine";

const TutorialContext = createContext(null);
const PROGRESS_KEY_PREFIX = "tutorial_module_";
const RESUME_KEY_PREFIX = "tutorial_resume_";
const HANDOFF_DELAY_MS = 1500;
const TUTORIALS_RETURN_ROUTE = "/settings/tutorials";

export function TutorialProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTutorialId, setActiveTutorialId] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [guideSpotlightRects, setGuideSpotlightRects] = useState([]);
  const [targetProgressLabel, setTargetProgressLabel] = useState(null);
  const [typedNextUnlocked, setTypedNextUnlocked] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [completedModules, setCompletedModules] = useState(() => loadCompletedModules());
  const [tutorialProgress, setTutorialProgress] = useState(() => loadTutorialProgress());
  const [sequenceIds, setSequenceIds] = useState([]);
  const [minStepIndex, setMinStepIndex] = useState(0);
  const [notice, setNotice] = useState(null);
  const handoffTimerRef = useRef(null);
  const tutorialsReturnScrollRef = useRef(null);
  const firstPatientCreatedIdRef = useRef(null);
  const sequenceOnceKeysRef = useRef(new Set());

  const activeTutorial = activeTutorialId ? getTutorialById(activeTutorialId) : null;
  const steps = useMemo(() => {
    const tutorialSteps = activeTutorial?.steps || [];
    if (!Array.isArray(activeRun?.stepIndexes) || activeRun.stepIndexes.length === 0) return tutorialSteps;
    return activeRun.stepIndexes
      .map((index) => tutorialSteps[index])
      .filter(Boolean);
  }, [activeRun?.stepIndexes, activeTutorial]);
  const activeStep = steps[stepIndex] || null;
  const visibleStep = useMemo(() => {
    if (!activeStep) return activeStep;
    const step = targetProgressLabel
      ? { ...activeStep, progressLabel: targetProgressLabel }
      : activeStep;
    if (typedNextUnlocked && isTypedGateStep(activeStep)) {
      return { ...step, hideNext: false, typedGateUnlocked: true };
    }
    return step;
  }, [activeStep, targetProgressLabel, typedNextUnlocked]);

  const exitTutorial = useCallback(() => {
    if (handoffTimerRef.current) {
      window.clearTimeout(handoffTimerRef.current);
      handoffTimerRef.current = null;
    }
    setActiveTutorialId(null);
    setActiveRun(null);
    setStepIndex(0);
    setSpotlightRect(null);
    setGuideSpotlightRects([]);
    setTargetProgressLabel(null);
    setTypedNextUnlocked(false);
    setConfirmationVisible(false);
    setSequenceIds([]);
    setMinStepIndex(0);
    setNotice(null);
    firstPatientCreatedIdRef.current = null;
    sequenceOnceKeysRef.current = new Set();
  }, []);

  useEffect(() => () => {
    if (handoffTimerRef.current) window.clearTimeout(handoffTimerRef.current);
  }, []);

  const markLocalTutorialCompleted = useCallback((tutorialId) => {
    if (!tutorialId) return false;
    try {
      window.localStorage.setItem(`${PROGRESS_KEY_PREFIX}${tutorialId}`, "completed");
      window.localStorage.removeItem(`${RESUME_KEY_PREFIX}${tutorialId}`);
    } catch {
      // Local progress is a convenience only.
    }
    setCompletedModules((current) => ({ ...current, [tutorialId]: true }));
    setTutorialProgress((current) => {
      const next = { ...current };
      delete next[tutorialId];
      return next;
    });
    return true;
  }, []);

  const markTutorialCompleted = useCallback(async (tutorialId) => {
    if (!tutorialId) return false;
    const { error } = await supabase.rpc("mark_tutorial_flow_watched", {
      p_tutorial_id: tutorialId,
    });
    if (error) {
      // Keep the settings checklist database-owned. Local badges only move after
      // the backend accepts the watched-flow write.
      // eslint-disable-next-line no-console
      console.error("[Tutorial] Could not record watched tutorial flow", error);
      return false;
    }
    return markLocalTutorialCompleted(tutorialId);
  }, [markLocalTutorialCompleted]);

  const captureTutorialReturnPosition = useCallback(() => {
    if (typeof window === "undefined") return;
    tutorialsReturnScrollRef.current = {
      x: window.scrollX || 0,
      y: window.scrollY || 0,
    };
  }, []);

  const returnToTutorialsPage = useCallback(() => {
    const returnScroll = tutorialsReturnScrollRef.current;
    navigate(TUTORIALS_RETURN_ROUTE);
    if (!returnScroll || typeof window === "undefined") return;
    if (process.env.NODE_ENV === "test") {
      tutorialsReturnScrollRef.current = null;
      return;
    }
    window.setTimeout(() => {
      try {
        window.scrollTo(returnScroll.x, returnScroll.y);
      } catch {
        // Scroll restoration is best-effort after route handoff.
      }
      tutorialsReturnScrollRef.current = null;
    }, 0);
  }, [navigate]);

  const startTutorial = useCallback((tutorialId, options = {}) => {
    const tutorial = getTutorialById(tutorialId);
    if (!tutorial || tutorial.steps.length === 0) return;
    if (handoffTimerRef.current) {
      window.clearTimeout(handoffTimerRef.current);
      handoffTimerRef.current = null;
    }
    const progressId = options.progressId || tutorialId;
    const stepIndexes = Array.isArray(options.stepIndexes) && options.stepIndexes.length > 0
      ? options.stepIndexes.filter((index) => Number.isInteger(index) && index >= 0 && index < tutorial.steps.length)
      : null;
    const stepCount = stepIndexes?.length || tutorial.steps.length;
    const savedProgress = options.resume ? tutorialProgress[progressId] : null;
    const requestedStepIndex = savedProgress?.tutorialId === tutorialId
      ? Math.min(Math.max(Number(savedProgress.stepIndex) || 0, 0), Math.max(0, stepCount - 1))
      : 0;
    const savedStepIndex = safeResumeStepIndex(tutorialId, requestedStepIndex, stepIndexes);
    captureTutorialReturnPosition();
    setActiveTutorialId(tutorialId);
    setActiveRun({
      progressId,
      stepIndexes,
      persistCompletion: options.persistCompletion !== false,
    });
    setStepIndex(savedStepIndex);
    setSpotlightRect(null);
    setGuideSpotlightRects([]);
    setTargetProgressLabel(null);
    setTypedNextUnlocked(false);
    setConfirmationVisible(false);
    setSequenceIds([]);
    setMinStepIndex(0);
    setNotice(null);
    firstPatientCreatedIdRef.current = null;
    sequenceOnceKeysRef.current = new Set();
  }, [captureTutorialReturnPosition, tutorialProgress]);

  const startTutorialSequence = useCallback((ids) => {
    const sequence = Array.isArray(ids) ? ids : [];
    const first = sequence.map((id) => getTutorialById(id)).find((tutorial) => tutorial?.steps?.length > 0);
    if (!first) return;
    if (handoffTimerRef.current) {
      window.clearTimeout(handoffTimerRef.current);
      handoffTimerRef.current = null;
    }
    captureTutorialReturnPosition();
    setSequenceIds(sequence);
    setNotice(null);
    setActiveTutorialId(first.id);
    setActiveRun(null);
    setStepIndex(0);
    setMinStepIndex(0);
    setSpotlightRect(null);
    setGuideSpotlightRects([]);
    setTargetProgressLabel(null);
    setTypedNextUnlocked(false);
    setConfirmationVisible(false);
    firstPatientCreatedIdRef.current = null;
    sequenceOnceKeysRef.current = new Set();
  }, [captureTutorialReturnPosition]);

  const finishActiveTutorial = useCallback(async () => {
    if (!activeTutorial) return;
    const progressId = activeRun?.progressId || activeTutorial.id;
    const completed = activeRun?.persistCompletion === false
      ? markLocalTutorialCompleted(progressId)
      : await markTutorialCompleted(progressId);
    if (!completed) return;
    const createdPatientId = activeTutorial.id === "patient-registration"
      ? firstPatientCreatedIdRef.current
      : null;
    if (createdPatientId) {
      firstPatientCreatedIdRef.current = null;
      exitTutorial();
      navigate(`/patients/${createdPatientId}`);
      return;
    }

    if (sequenceIds.length > 0) {
      const currentIndex = sequenceIds.indexOf(activeTutorial.id);
      const nextTutorial = sequenceIds
        .slice(currentIndex + 1)
        .map((id) => getTutorialById(id))
        .find((tutorial) => tutorial?.steps?.length > 0);

      setActiveTutorialId(null);
      setActiveRun(null);
      setStepIndex(0);
      setMinStepIndex(0);
      setSpotlightRect(null);
      setGuideSpotlightRects([]);
      setTargetProgressLabel(null);
      setTypedNextUnlocked(false);
      setConfirmationVisible(false);

      if (nextTutorial) {
        const nextMinStepIndex = countLeadingSequenceOnceSteps(nextTutorial, sequenceOnceKeysRef.current);
        setNotice({
          kind: "handoff",
          message: `${activeTutorial.name} complete. Starting ${nextTutorial.name} →`,
        });
        handoffTimerRef.current = window.setTimeout(() => {
          setNotice(null);
          setActiveTutorialId(nextTutorial.id);
          setActiveRun(null);
          setStepIndex(nextMinStepIndex);
          setMinStepIndex(nextMinStepIndex);
          setSpotlightRect(null);
          setGuideSpotlightRects([]);
          setTargetProgressLabel(null);
          setTypedNextUnlocked(false);
          handoffTimerRef.current = null;
        }, HANDOFF_DELAY_MS);
      } else {
        exitTutorial();
        returnToTutorialsPage();
      }
      return;
    }

    exitTutorial();
    returnToTutorialsPage();
  }, [activeRun, activeTutorial, exitTutorial, markLocalTutorialCompleted, markTutorialCompleted, navigate, returnToTutorialsPage, sequenceIds]);

  useEffect(() => {
    if (!activeTutorial || !activeStep || sequenceIds.length > 0) return;
    const progressId = activeRun?.progressId || activeTutorial.id;
    const progress = {
      tutorialId: activeTutorial.id,
      stepIndex,
      stepCount: steps.length,
      updatedAt: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(`${RESUME_KEY_PREFIX}${progressId}`, JSON.stringify(progress));
    } catch {
      // Resume is a local convenience only.
    }
    setTutorialProgress((current) => {
      const existing = current[progressId];
      if (existing?.tutorialId === progress.tutorialId
        && existing?.stepIndex === progress.stepIndex
        && existing?.stepCount === progress.stepCount) return current;
      return { ...current, [progressId]: progress };
    });
  }, [activeRun?.progressId, activeStep, activeTutorial, sequenceIds.length, stepIndex, steps.length]);

  const moveStep = useCallback((direction) => {
    if (!steps.length) return;
    const next = stepIndex + direction;
    if (direction > 0 && activeStep?.sequenceOnceKey && sequenceIds.length > 0) {
      sequenceOnceKeysRef.current.add(activeStep.sequenceOnceKey);
    }
    if (next < minStepIndex || next >= steps.length) {
      if (direction > 0) finishActiveTutorial();
      return;
    }
    const destinationStep = steps[next];
    if (
      destinationStep?.route
      && !routeMatches(location.pathname, location.search, destinationStep.route)
    ) {
      // Start a cross-page handoff inside the same user action that moved the
      // tutorial. Waiting for the active-step effect can leave the address bar
      // on the next route while React is still showing the previous surface
      // (most visibly Checkout -> Chairside between MV visits).
      navigate(destinationStep.route);
    }
    setStepIndex(next);
  }, [activeStep, finishActiveTutorial, location.pathname, location.search, minStepIndex, navigate, sequenceIds.length, stepIndex, steps]);

  useEffect(() => {
    if (!activeStep || sequenceIds.length === 0 || !activeStep.sequenceOnceKey) return;
    sequenceOnceKeysRef.current.add(activeStep.sequenceOnceKey);
  }, [activeStep, sequenceIds.length]);

  useEffect(() => {
    setConfirmationVisible(false);
    setTargetProgressLabel(formatCompletionProgress(activeStep, 0));
    setTypedNextUnlocked(false);
  }, [activeStep]);

  useEffect(() => {
    if (!activeStep) return undefined;
    if (!routeMatches(location.pathname, location.search, activeStep.route)) {
      navigate(activeStep.route);
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;
    let retryId = 0;
    const settleTimeoutIds = [];
    let attempts = 0;
    let sideSwitchAttempted = false;
    const maxAttempts = 6;
    const updateRects = () => {
      const freshTarget = findStepTarget(activeStep);
      if (!freshTarget) return;
      setSpotlightRect(calculateSpotlightRect(freshTarget, activeStep.spotlightPadding, {
        minWidth: activeStep.spotlightMinSize,
        minHeight: activeStep.spotlightMinSize,
        zoom: activeStep.spotlightZoom,
      }));
      setGuideSpotlightRects(calculateGuideSpotlightRects(activeStep));
    };
    const measure = () => {
      const target = findStepTarget(activeStep);
      if (!target) {
        // Compact chart layouts (< 900px or measured-narrow canvas) only
        // render one arch side at a time, so a tutorial step targeting a
        // tooth on the hidden side can't find its anchor. Click the matching
        // side toggle once and re-measure so the user follows along without
        // having to flip arches manually.
        if (!sideSwitchAttempted) {
          const requiredSide = getToothSideFromTargetId(activeStep.targetId);
          if (requiredSide) {
            const sideToggle = findTutorialTarget(`charting-side-${requiredSide}`);
            if (sideToggle) {
              sideSwitchAttempted = true;
              sideToggle.click();
              retryId = window.setTimeout(measure, 200);
              return;
            }
          }
        }
        if (attempts < maxAttempts) {
          attempts += 1;
          retryId = window.setTimeout(measure, 150);
        } else {
          findStepTarget(activeStep, { warn: true });
          const next = stepIndex + 1;
          if (next >= steps.length) exitTutorial();
          else setStepIndex(next);
        }
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      timeoutId = window.setTimeout(() => {
        updateRects();
        [140, 320, 640].forEach((delay) => {
          settleTimeoutIds.push(window.setTimeout(updateRects, delay));
        });
      }, 180);
    };

    rafId = window.requestAnimationFrame(measure);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryId);
      settleTimeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, [activeStep, exitTutorial, location.pathname, location.search, navigate, stepIndex, steps]);

  useEffect(() => {
    if (!activeStep) return undefined;
    function refreshRect() {
      const target = findStepTarget(activeStep);
      if (target) setSpotlightRect(calculateSpotlightRect(target, activeStep.spotlightPadding, {
        minWidth: activeStep.spotlightMinSize,
        minHeight: activeStep.spotlightMinSize,
        zoom: activeStep.spotlightZoom,
      }));
      setGuideSpotlightRects(calculateGuideSpotlightRects(activeStep));
    }
    const visualViewport = window.visualViewport;
    window.addEventListener("resize", refreshRect);
    window.addEventListener("orientationchange", refreshRect);
    window.addEventListener("scroll", refreshRect, true);
    visualViewport?.addEventListener("resize", refreshRect);
    visualViewport?.addEventListener("scroll", refreshRect);
    return () => {
      window.removeEventListener("resize", refreshRect);
      window.removeEventListener("orientationchange", refreshRect);
      window.removeEventListener("scroll", refreshRect, true);
      visualViewport?.removeEventListener("resize", refreshRect);
      visualViewport?.removeEventListener("scroll", refreshRect);
    };
  }, [activeStep]);

  useEffect(() => {
    if (!activeStep || !Array.isArray(activeStep.completionTargetIds) || activeStep.completionTargetIds.length === 0) {
      return undefined;
    }
    if (!routeMatches(location.pathname, location.search, activeStep.route)) return undefined;

    const refreshRects = () => {
      const target = findStepTarget(activeStep);
      if (target) {
        setSpotlightRect(calculateSpotlightRect(target, activeStep.spotlightPadding, {
          minWidth: activeStep.spotlightMinSize,
          minHeight: activeStep.spotlightMinSize,
          zoom: activeStep.spotlightZoom,
        }));
      }
      setGuideSpotlightRects(calculateGuideSpotlightRects(activeStep));
    };
    const timeoutIds = [];
    const rafId = window.requestAnimationFrame(refreshRects);
    [80, 180, 360].forEach((delay) => {
      timeoutIds.push(window.setTimeout(refreshRects, delay));
    });
    return () => {
      window.cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, [activeStep, location.pathname, location.search, targetProgressLabel]);

  useEffect(() => {
    if (!activeStep || !routeMatches(location.pathname, location.search, activeStep.route)) return undefined;
    const eventName = activeStep.triggerEvent || "click";
    let advanceTimeout = 0;
    let retryTimeout = 0;
    let attempts = 0;
    let boundTargets = [];
    let animatedTargets = [];
    let guideTargets = [];
    let hasAdvanced = false;
    const completedTargetIds = new Set();
    const maxAttempts = 12;
    const stopTargetAnimation = () => {
      animatedTargets.forEach((target) => {
        target.classList.remove("tutorial-target-pop");
        target.classList.remove("tutorial-target-surface-emphasis");
      });
      guideTargets.forEach((target) => {
        target.classList.remove("tutorial-target-guide");
      });
      animatedTargets = [];
      guideTargets = [];
    };
    const advance = () => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      stopTargetAnimation();
      window.clearTimeout(advanceTimeout);
      advanceTimeout = window.setTimeout(() => {
        moveStep(1);
      }, activeStep.advanceDelayMs ?? 300);
    };
    const targetHasCompletionValue = () => {
      if (activeStep.completionValue == null) return false;
      const target = findStepTarget(activeStep);
      return target && String(target.value ?? "") === String(activeStep.completionValue);
    };
    const maybeAutoSwitchSideForCompletion = () => {
      // Batch steps (Today's composite findings, etc.) accept clicks across
      // multiple teeth. On compact viewports only one arch side is rendered,
      // so once the visible-side targets are completed the user can't reach
      // the rest. Flip the chart for them and re-bind so new teeth get the
      // surface-emphasis guide treatment.
      if (completionTargetIds.length === 0) return;
      const incomplete = completionTargetIds.filter((id) => !completedTargetIds.has(id));
      if (incomplete.length === 0) return;
      if (incomplete.some((id) => findTutorialTarget(id))) return;
      for (const id of incomplete) {
        const side = getToothSideFromTargetId(id);
        if (!side) continue;
        const toggle = findTutorialTarget(`charting-side-${side}`);
        if (!toggle) continue;
        toggle.click();
        window.setTimeout(() => bindTargets(false), 200);
        return;
      }
    };
    const advanceIfEventTargetMatches = (event) => {
      if (activeStep.interceptTargetNavigation && event.type !== eventName) return;
      if (completionTargetIds.length > 0) {
        const matchedTargetId = getMatchedTutorialTargetId(event, completionTargetIds);
        if (!matchedTargetId) return;
        completedTargetIds.add(matchedTargetId);
        setTargetProgressLabel(formatCompletionProgress(activeStep, completedTargetIds.size));
        if (completedTargetIds.size >= completionTargetIds.length) {
          advance();
        } else {
          maybeAutoSwitchSideForCompletion();
        }
        return;
      }
      if (eventMatchesTutorialTarget(event, targetIds)) {
        if (activeStep.interceptTargetNavigation) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation?.();
        }
        advance();
      }
    };
    const blockDisallowedSurfaceEvent = (event) => {
      if (!shouldGateSurfaceTargets(targetIds)) return;
      if (!eventTargetsTutorialSurface(event)) return;
      if (eventMatchesTutorialTarget(event, targetIds)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const completionTargetIds = Array.isArray(activeStep.completionTargetIds) ? activeStep.completionTargetIds : [];
    const targetIds = getTutorialInteractiveTargetIds(activeStep);
    if (targetIds.length === 0) {
      warnIfClickInstructionMissingTrigger(activeStep);
      if (!eventName.startsWith("tutorial:")) return undefined;
    }

    const bindTargets = (withInteractionListeners = true) => {
      const targets = targetIds.map((targetId) => findTutorialTarget(targetId)).filter(Boolean);
      if (!targets.length) {
        if (attempts < maxAttempts) {
          attempts += 1;
          retryTimeout = window.setTimeout(() => bindTargets(withInteractionListeners), 100);
        }
        return;
      }
      boundTargets = targets;
      animatedTargets = targets;
      boundTargets.forEach((target) => {
        if (isSurfaceSpotlightTarget(target)) {
          target.classList.add("tutorial-target-surface-emphasis");
        } else {
          target.classList.add("tutorial-target-pop");
        }
      });
      guideTargets = getGuideTargets(activeStep)
        .filter((target) => !targets.includes(target));
      guideTargets.forEach((target) => {
        target.classList.add("tutorial-target-guide");
      });
    };

    if (eventName.startsWith("tutorial:")) {
      bindTargets(false);
      const isTypedGate = Number(activeStep.typedMinLength) > 0;
      const updateTypedGate = () => {
        setTypedNextUnlocked(tutorialTargetHasTypedValue(findStepTarget(activeStep), activeStep.typedMinLength));
      };
      const handleCustomTutorialEvent = (event) => {
        if (isTypedGate) {
          updateTypedGate();
          return;
        }
        if (eventName === "tutorial:first-patient-created") {
          const patientId = event?.detail?.patient_id;
          if (!patientId) return;
          firstPatientCreatedIdRef.current = event?.detail?.tutorial_demo ? null : patientId;
        }
        advance();
      };
      const filledTargetCheckIds = isTypedGate
        ? [40, 180, 420].map((delay) => window.setTimeout(() => {
          updateTypedGate();
        }, delay))
        : [40, 180, 420].map((delay) => window.setTimeout(() => {
          if (targetHasCompletionValue()) advance();
        }, delay));
      window.addEventListener(eventName, handleCustomTutorialEvent);
      window.addEventListener("tutorial:missing-confirmation-requested", stopTargetAnimation);
      return () => {
        window.clearTimeout(advanceTimeout);
        window.clearTimeout(retryTimeout);
        filledTargetCheckIds.forEach((id) => window.clearTimeout(id));
        stopTargetAnimation();
        window.removeEventListener(eventName, handleCustomTutorialEvent);
        window.removeEventListener("tutorial:missing-confirmation-requested", stopTargetAnimation);
      };
    }

    bindTargets();
    const gatedEventNames = Array.from(new Set([eventName, "click", "pointerup", "touchend"]));
    gatedEventNames.forEach((name) => {
      document.addEventListener(name, blockDisallowedSurfaceEvent, true);
    });
    document.addEventListener(eventName, advanceIfEventTargetMatches, true);
    document.addEventListener("pointerup", advanceIfEventTargetMatches, true);
    document.addEventListener("touchend", advanceIfEventTargetMatches, true);
    return () => {
      window.clearTimeout(advanceTimeout);
      window.clearTimeout(retryTimeout);
      stopTargetAnimation();
      gatedEventNames.forEach((name) => {
        document.removeEventListener(name, blockDisallowedSurfaceEvent, true);
      });
      document.removeEventListener(eventName, advanceIfEventTargetMatches, true);
      document.removeEventListener("pointerup", advanceIfEventTargetMatches, true);
      document.removeEventListener("touchend", advanceIfEventTargetMatches, true);
    };
  }, [activeStep, location.pathname, location.search, moveStep]);

  useEffect(() => {
    if (!activeStep?.confirmation) return undefined;
    const showConfirmation = () => setConfirmationVisible(true);
    window.addEventListener("tutorial:missing-confirmation-requested", showConfirmation);
    return () => window.removeEventListener("tutorial:missing-confirmation-requested", showConfirmation);
  }, [activeStep]);

  const confirmTutorialAction = useCallback(() => {
    setConfirmationVisible(false);
    window.dispatchEvent(new CustomEvent("tutorial:missing-confirmation-confirmed"));
  }, []);

  const cancelTutorialAction = useCallback(() => {
    setConfirmationVisible(false);
    window.dispatchEvent(new CustomEvent("tutorial:missing-confirmation-cancelled"));
  }, []);

  const value = useMemo(() => ({
    activeTutorialId,
    startTutorial,
    startTutorialSequence,
    exitTutorial,
    completedModules,
    tutorialProgress,
    markTutorialCompleted,
  }), [activeTutorialId, completedModules, exitTutorial, markTutorialCompleted, startTutorial, startTutorialSequence, tutorialProgress]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
      <TutorialOverlay
        open={!!activeTutorial && !!activeStep}
        tutorialId={activeTutorialId}
        notice={notice}
        step={visibleStep}
        spotlightRect={spotlightRect}
        guideSpotlightRects={guideSpotlightRects}
        stepIndex={Math.max(0, stepIndex - minStepIndex)}
        stepCount={Math.max(0, steps.length - minStepIndex)}
        canGoBack={stepIndex > minStepIndex}
        onPrev={() => moveStep(-1)}
        onNext={() => moveStep(1)}
        onExit={exitTutorial}
        confirmationVisible={confirmationVisible}
        onConfirm={confirmTutorialAction}
        onCancel={cancelTutorialAction}
      />
    </TutorialContext.Provider>
  );
}

function calculateGuideSpotlightRects(step) {
  return getGuideTargets(step).map((target) => calculateSpotlightRect(target, step.spotlightPadding ?? 4, {
    minWidth: step.spotlightMinSize,
    minHeight: step.spotlightMinSize,
    zoom: step.spotlightZoom,
  }));
}

function findStepTarget(step, options = {}) {
  return findTutorialTarget(step?.targetId, {
    ...options,
    fallbackTargetIds: step?.fallbackTargetIds,
  });
}

function getGuideTargets(step) {
  if (!Array.isArray(step?.guideTargetIds) || step.guideTargetIds.length === 0) return [];
  return step.guideTargetIds
    .filter((targetId) => targetId !== step.targetId)
    .map((targetId) => findTutorialTarget(targetId))
    .filter(Boolean);
}

function formatCompletionProgress(step, completedCount) {
  if (!Array.isArray(step?.completionTargetIds) || step.completionTargetIds.length === 0) return null;
  const total = step.completionTargetIds.length;
  const safeCompleted = Math.min(Math.max(completedCount || 0, 0), total);
  return `${step.progressLabelPrefix || "Targets"} ${safeCompleted}/${total}`;
}

function tutorialTargetHasTypedValue(target, minLength = 1) {
  if (!target) return false;
  const requiredLength = Number.isFinite(Number(minLength)) ? Math.max(1, Number(minLength)) : 1;
  const value = target.value;
  return typeof value === "string" && value.trim().length >= requiredLength;
}

function isTypedGateStep(step) {
  return typeof step?.triggerEvent === "string"
    && step.triggerEvent.startsWith("tutorial:")
    && Number(step.typedMinLength) > 0;
}

function countLeadingSequenceOnceSteps(tutorial, seenKeys) {
  let count = 0;
  for (const step of tutorial?.steps || []) {
    if (!step.sequenceOnceKey || !seenKeys?.has(step.sequenceOnceKey)) break;
    count += 1;
  }
  return count;
}

function loadCompletedModules() {
  if (typeof window === "undefined") return {};
  const completed = {};
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(PROGRESS_KEY_PREFIX) && window.localStorage.getItem(key) === "completed") {
        completed[key.slice(PROGRESS_KEY_PREFIX.length)] = true;
      }
    }
  } catch {
    return {};
  }
  return completed;
}

function loadTutorialProgress() {
  if (typeof window === "undefined") return {};
  const progress = {};
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(RESUME_KEY_PREFIX)) continue;
      const progressId = key.slice(RESUME_KEY_PREFIX.length);
      const saved = JSON.parse(window.localStorage.getItem(key));
      if (!progressId || !saved?.tutorialId || !Number.isInteger(saved?.stepIndex)) continue;
      progress[progressId] = saved;
    }
  } catch {
    return {};
  }
  return progress;
}

function safeResumeStepIndex(tutorialId, savedStepIndex, stepIndexes) {
  if (Array.isArray(stepIndexes) && stepIndexes.length > 0) return savedStepIndex;
  // These workflows open a modal from an earlier page action. A browser refresh
  // closes the modal, so resume at the action that can reconstruct the form.
  if (tutorialId === "patient-registration" && savedStepIndex > 2) return 2;
  if (tutorialId === "booking-calendar" && savedStepIndex > 1) return 1;
  if (tutorialId === "front-desk-routine-checkout" && savedStepIndex > 4) return 4;
  return savedStepIndex;
}

function warnIfClickInstructionMissingTrigger(step) {
  if (!step?.hideNext) return;
  const text = `${step?.heading || ""} ${step?.body || ""}`;
  if (!/\b(tap|click|pick|select|switch|load)\b/i.test(text)) return;
  if (process.env.NODE_ENV === "production") return;
  // eslint-disable-next-line no-console
  console.error(`[Tutorial] Click instruction missing trigger target: ${step.id}`);
}

function isSurfaceSpotlightTarget(target) {
  const shape = target?.dataset?.tutorialSpotlightShape;
  return (shape && shape !== "rect") || target?.dataset?.tutorialId?.includes("-surface-");
}

function shouldGateSurfaceTargets(targetIds) {
  return targetIds.some((targetId) => targetId.includes("-surface-"));
}

function eventTargetsTutorialSurface(event) {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const nodes = path.length > 0 ? path : [event.target];
  return nodes.some((node) => {
    if (!node || node.nodeType !== 1) return false;
    const tutorialId = node.dataset?.tutorialId || node.closest?.("[data-tutorial-id]")?.dataset?.tutorialId;
    return /^charting-tooth-\d+-surface-[a-z]+$/i.test(tutorialId || "");
  });
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used inside TutorialProvider");
  }
  return context;
}
